import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export function MultiSelect({ options, selected, onChange, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter(v => v !== id) : [...selected, id]);
  };

  const names = selected.map(id => options.find(o => o.id === id)?.name).filter(Boolean);
  const label = names.length === 0
    ? placeholder
    : names.length <= 2
      ? names.join(', ')
      : `${names.length} selected`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full h-9 px-2 border border-[#E0E0E0] rounded-md text-sm bg-white flex items-center justify-between gap-1 text-left"
        data-testid={testId}
      >
        <span className={`truncate ${selected.length ? 'text-[#111111]' : 'text-[#999999]'}`}>{label}</span>
        <ChevronDown size={14} className="shrink-0 text-[#999999]" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-auto bg-white border border-[#E0E0E0] rounded-md shadow-lg py-1" data-testid={`${testId}-dropdown`}>
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full text-left px-3 py-1.5 text-xs text-[#FF6B35] hover:bg-[#F5F5F5]"
              data-testid={`${testId}-clear`}
            >
              Clear selection
            </button>
          )}
          {options.length === 0 && <p className="px-3 py-2 text-xs text-[#999999]">Nothing to select</p>}
          {options.map(opt => (
            <button
              type="button"
              key={opt.id}
              onClick={() => toggle(opt.id)}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-[#111111] hover:bg-[#F5F5F5] text-left"
              data-testid={`${testId}-option-${opt.id}`}
            >
              <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${selected.includes(opt.id) ? 'bg-[#CFFF40] border-[#CFFF40]' : 'border-[#CCCCCC]'}`}>
                {selected.includes(opt.id) && <Check size={12} className="text-[#101010]" />}
              </span>
              <span className="truncate">{opt.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
